import { forwardRef } from "react";

import { Link } from "../link/Link";
import { Icon } from "../icon/Icon";

import type { LinkProps } from "../link/Link";
import type { TabProps } from "./Tab";

export interface TabLinkProps {
  children?: React.ReactNode;
  count?: TabProps["count"];
  href: string;
  icon?: TabProps["icon"];
  id?: string;
  isSelected?: boolean;
  nativeBehavior?: LinkProps["nativeBehavior"];
  nativeStencil?: LinkProps["nativeStencil"];
  notification?: boolean;
  preventScrollReset?: boolean;
  onClick?: (event: React.SyntheticEvent) => void;
}

export const TabLink = forwardRef<HTMLAnchorElement, TabLinkProps>(
  function TabLink(
    {
      children,
      count,
      href,
      icon,
      id,
      isSelected,
      nativeBehavior,
      nativeStencil,
      notification,
      preventScrollReset,
      onClick,
    },
    ref,
  ) {
    const scrollIntoView = (element: Element) => {
      // Allow the browser to paint before scrolling
      requestAnimationFrame(() => {
        element.scrollIntoView({
          block: "nearest",
          inline: "center",
          behavior: "smooth",
        });
      });
    };

    const handleClick = (e: React.MouseEvent) => {
      scrollIntoView(e.target as Element);
      onClick?.(e);
    };

    const handleFocus = (e: React.FocusEvent) => {
      scrollIntoView(e.target);
    };

    return (
      <Link
        aria-current={isSelected ? "page" : undefined}
        className="Tab"
        id={id}
        nativeBehavior={nativeBehavior}
        nativeStencil={nativeStencil}
        preventScrollReset={preventScrollReset}
        state={{ isTab: true }}
        to={href}
        ref={ref}
        onClick={handleClick}
        onFocus={handleFocus}
      >
        {icon && <Icon icon={icon} size="md" />}
        {children}
        {count != null && <span className="Tab__count">{count}</span>}
        {notification && <span className="Tab__notification" />}
      </Link>
    );
  },
);
